// Pure Near You helpers — no React, no network — so the open/closed
// logic and formatting are unit-testable (test/near-helpers.test.js).

export const FILTERS = [
  { k: 'all', label: 'All' },
  { k: 'store', label: 'Stores' },
  { k: 'club', label: 'Clubs' },
  { k: 'event', label: 'Events' },
  { k: 'frag', label: 'Frags' },
  { k: 'online', label: 'Online' },
];

export const RADII = [10, 25, 50, 100];
// Round kilometre steps near the mile ones, for a keeper in km. The feed
// still asks in miles.
export const KM_RADII = [15, 40, 80, 160];
export const KM_PER_MI = 1.609344;

export function radiusChoices(unit = 'mi') {
  if (unit !== 'km') return RADII.map((mi) => ({ label: `${mi} mi`, mi }));
  return KM_RADII.map((km) => ({
    label: `${km} km`,
    mi: Math.round((km / KM_PER_MI) * 100) / 100,
  }));
}

export const EVENT_KINDS = [
  { k: 'meetup', label: 'Club meetup' },
  { k: 'frag_swap', label: 'Frag swap' },
  { k: 'sale', label: 'Store sale' },
  { k: 'other', label: 'Other' },
];

export const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
export const DAY_LABELS = [
  ['mon', 'Mon'],
  ['tue', 'Tue'],
  ['wed', 'Wed'],
  ['thu', 'Thu'],
  ['fri', 'Fri'],
  ['sat', 'Sat'],
  ['sun', 'Sun'],
];
const DOW_LABEL = {
  sun: 'Sun',
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
};

export function capsOf(place, labels) {
  const caps = place?.capabilities || {};
  return Object.keys(labels || {}).filter((k) => caps[k]);
}

// The origin is deliberately fuzzed to city level and never stored, so
// sub-mile precision is fabricated. A store geocoded to the same city
// centroid rendered a literal "0 mi", which reads as "at your doorstep"
// rather than "somewhere in your town". Same ground-truth rule the score
// follows: never imply more accuracy than the data supports.
export function distanceLabel(mi, unit = 'mi') {
  const n = Number(mi);
  if (mi == null || !Number.isFinite(n) || n < 0) return null;
  const u = unit === 'km' ? 'km' : 'mi';
  const v = u === 'km' ? n * KM_PER_MI : n;
  if (v < 1) return `< 1 ${u}`;
  if (v < 10) return `${Math.round(v * 10) / 10} ${u}`;
  return `${Math.round(v)} ${u}`;
}

// A store that ticks every box shows fifteen identical chips, which
// differentiates nothing and buries the buttons. Ordering by what a reef
// keeper actually travels for means the first few chips carry the signal
// and the rest can collapse behind a toggle.
export const CAP_PRIORITY = [
  'premium_sps',
  'lps',
  'soft_coral',
  'saltwater',
  'live_rock',
  'inverts',
  'saltwater_fill',
  'rodi_fill',
  'quarantined_fish',
  'water_testing',
  'dry_goods',
  'fish_food',
  'freshwater',
  'plants',
  'fw_inverts',
];

export function orderedCaps(place, labels) {
  const rank = (k) => {
    const i = CAP_PRIORITY.indexOf(k);
    return i === -1 ? CAP_PRIORITY.length : i;
  };
  return capsOf(place, labels).sort((a, b) => rank(a) - rank(b));
}

// Formatters are slow to build and a list of stores asks for the same
// few on every render (and every keystroke in the search box).
const formatters = new Map();
function formatter(locale, opts) {
  const key = `${locale || ''}|${JSON.stringify(opts)}`;
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, opts);
    if (formatters.size > 200) formatters.clear();
    formatters.set(key, f);
  }
  return f;
}

// A zone this browser can format in, or null (an old browser, a typo an
// admin saved): the caller then reads the viewer's own clock.
function knownZone(tz) {
  if (!tz || typeof tz !== 'string') return null;
  try {
    formatter('en-US', { timeZone: tz });
    return tz;
  } catch {
    return null;
  }
}

// "America/Chicago" -> "Chicago time": a place every keeper can read, the
// same in winter and summer and in every language setting ("CDT" or
// "GMT-5" is what the phone itself would print).
export function zoneName(tz) {
  const zone = knownZone(tz);
  if (!zone || !zone.includes('/')) return '';
  return `${zone.split('/').pop().replace(/_/g, ' ')} time`;
}

// The keeper's own zone, sent with an event they add.
export function viewerZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

// An event's date and time read on the clock where it is held (its tz):
// on the viewer's clock a Chicago swap at 7 pm read 5 pm from Los Angeles.
export function eventDate(iso, tz = null) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const zone = knownZone(tz);
  return formatter(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(zone ? { timeZone: zone } : {}),
  }).format(d);
}

export function eventTime(iso, tz = null) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const zone = knownZone(tz);
  return formatter(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    ...(zone ? { timeZone: zone } : {}),
  }).format(d);
}

// The place's zone after a time, only when its clock reads differently
// from the viewer's at that moment ("7:00 PM Chicago time" to a keeper in
// Los Angeles, nothing to one in Chicago).
export function zoneTag(tz, at = new Date()) {
  const zone = knownZone(tz);
  const d = new Date(at);
  if (!zone || isNaN(d)) return '';
  try {
    const wallOpts = {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23',
    };
    const there = formatter('en-US', { ...wallOpts, timeZone: zone }).format(d);
    if (there === formatter('en-US', wallOpts).format(d)) return '';
    const name = zoneName(zone);
    return name ? ` ${name}` : '';
  } catch {
    return '';
  }
}

// An event is listed until it ends, so a swap that is on right now stays on
// the list. With no end time on file it counts as on for 3 hours, the same
// window the server uses (db/near.js listUpcomingPlaceEvents).
export const EVENT_DEFAULT_HOURS = 3;
export function eventOnNow(e, now = new Date()) {
  const start = new Date(e?.starts_at).getTime();
  if (!Number.isFinite(start)) return false;
  const endRaw = e?.ends_at ? new Date(e.ends_at).getTime() : NaN;
  const end = Number.isFinite(endRaw) ? endRaw : start + EVENT_DEFAULT_HOURS * 3600 * 1000;
  const t = now.getTime();
  return start <= t && t < end;
}

export function fmtTime(t) {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  if (!Number.isFinite(h)) return t;
  const ap = h >= 12 ? 'PM' : 'AM';
  const hh = ((h + 11) % 12) + 1;
  return m ? `${hh}:${String(m).padStart(2, '0')} ${ap}` : `${hh} ${ap}`;
}

function toMins(t) {
  const [h, m] = String(t).split(':').map(Number);
  return Number.isFinite(h) ? h * 60 + (m || 0) : null;
}

// Wall-clock (weekday + minutes) inside an IANA timezone. Null when the
// tz is missing/invalid so the caller falls back to the viewer's clock.
function nowInTz(now, tz) {
  if (!tz) return null;
  try {
    const parts = formatter('en-US', {
      timeZone: tz,
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    }).formatToParts(now);
    const get = (t) => parts.find((p) => p.type === t)?.value;
    const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'));
    const h = Number(get('hour')) % 24; // hour12:false can render midnight as "24"
    const m = Number(get('minute'));
    if (dow < 0 || !Number.isFinite(h) || !Number.isFinite(m)) return null;
    return { dow, mins: h * 60 + m };
  } catch {
    return null;
  }
}

// Live open/closed status from a per-day schedule. Returns
// { open, label } for a structured schedule, { text } for legacy
// free-text hours, or null with no hours at all. The clock is read in
// the STORE's timezone when it has one (tz) — a keeper browsing a
// remote city via ZIP search sees the store's local status — falling
// back to the viewer's clock (same-timezone browsing, the common case).
//
// zoneLabel names the store's zone after each time when it differs from
// the viewer's ("closes 9 PM CDT"), so a remote keeper is not misled.
export function openState(hours, { now = new Date(), tz = null, zoneLabel = false } = {}) {
  if (!hours) return null;
  if (hours.text) return { text: hours.text };
  if (!hours.days) return null;
  const tag = zoneLabel ? zoneTag(tz, now) : '';
  const at = (t) => `${fmtTime(t)}${tag}`;
  const dayKey = (i) => DAY_KEYS[((i % 7) + 7) % 7];
  const local = nowInTz(now, tz) || {
    dow: now.getDay(),
    mins: now.getHours() * 60 + now.getMinutes(),
  };
  // Yesterday's hours can run past midnight (6 PM to 1 AM): still open now.
  const yday = hours.days[dayKey(local.dow - 1)];
  if (yday && yday.open && yday.close) {
    const o = toMins(yday.open);
    const c = toMins(yday.close);
    if (o != null && c != null && c <= o && local.mins < c) {
      return { open: true, label: `Open · closes ${at(yday.close)}` };
    }
  }
  const today = hours.days[dayKey(local.dow)];
  if (today && today.open && today.close) {
    const o = toMins(today.open);
    const c = toMins(today.close);
    if (o != null && c != null) {
      const overnight = c <= o;
      if (local.mins >= o && (overnight || local.mins < c))
        return { open: true, label: `Open · closes ${at(today.close)}` };
      if (local.mins < o) return { open: false, label: `Opens ${at(today.open)}` };
    }
  }
  for (let i = 1; i <= 7; i++) {
    const d = hours.days[dayKey(local.dow + i)];
    if (d && d.open) {
      const when = i === 1 ? 'tomorrow' : DOW_LABEL[dayKey(local.dow + i)];
      return { open: false, label: `Opens ${when} ${at(d.open)}` };
    }
  }
  return { open: false, label: 'Closed' };
}

// The full week as rows for the expandable panel.
export function weekRows(hours) {
  if (!hours?.days) return null;
  return DAY_LABELS.map(([k, label]) => {
    const v = hours.days[k];
    return {
      label,
      txt: v && v.open && v.close ? `${fmtTime(v.open)} – ${fmtTime(v.close)}` : 'Closed',
    };
  });
}

// When a club meets, from the per-day editor the club form saves ("Tue
// 7 PM – 9 PM · Sat 10 AM – 12:30 PM"), or the older free-text line. The
// club card only read the text, so every club added in the app showed no
// meeting time at all.
export function meetsLine(hours) {
  if (!hours) return '';
  if (typeof hours.text === 'string' && hours.text.trim()) return hours.text.trim();
  if (!hours.days) return '';
  return DAY_LABELS.filter(([k]) => hours.days[k]?.open && hours.days[k]?.close)
    .map(
      ([k, label]) => `${label} ${fmtTime(hours.days[k].open)} – ${fmtTime(hours.days[k].close)}`
    )
    .join(' · ');
}

// ─── Smart form defaults ───

function toLocalInput(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Reef events are almost always weekends — default a new event to the
// upcoming Saturday at 10:00 (local), so the datetime picker isn't blank.
// Returns a <input type="datetime-local"> value string.
export function nextWeekendDefault(now = new Date()) {
  const d = new Date(now);
  d.setHours(10, 0, 0, 0);
  const day = d.getDay(); // 0 Sun .. 6 Sat
  let add = (6 - day + 7) % 7;
  if (add === 0 && now.getHours() >= 10) add = 7; // past 10am Saturday -> next Saturday
  d.setDate(d.getDate() + add);
  return toLocalInput(d);
}

// A datetime-local string + hours -> a new datetime-local string.
export function plusHours(dtLocal, hours) {
  if (!dtLocal) return '';
  const d = new Date(dtLocal);
  if (isNaN(d.getTime())) return '';
  d.setHours(d.getHours() + hours);
  return toLocalInput(d);
}

// Typical local-fish-store hours, one tap instead of twelve: closed
// Monday, Tue-Fri 11-7, Sat 10-6, Sun 11-5. The keeper adjusts from here.
export function typicalShopHours() {
  return {
    days: {
      tue: { open: '11:00', close: '19:00' },
      wed: { open: '11:00', close: '19:00' },
      thu: { open: '11:00', close: '19:00' },
      fri: { open: '11:00', close: '19:00' },
      sat: { open: '10:00', close: '18:00' },
      sun: { open: '11:00', close: '17:00' },
    },
  };
}

// The keeper's set location "Clarendon Hills, Illinois" -> "Clarendon
// Hills" as a city default for the frag form. Skips the GPS placeholder
// label ("your area").
export function cityFromLabel(label) {
  if (!label || label === 'your area') return '';
  return String(label).split(',')[0].trim();
}

// Remember the frag seller's contact so they don't retype it each listing.
const NEAR_CONTACT_KEY = 'aqs.nearContact';
export function readSavedContact() {
  try {
    return localStorage.getItem(NEAR_CONTACT_KEY) || '';
  } catch {
    return '';
  }
}
export function writeSavedContact(v) {
  try {
    if (v && v.trim()) localStorage.setItem(NEAR_CONTACT_KEY, v.trim());
  } catch {
    /* private mode — best effort */
  }
}

export function priceLabel(p) {
  if (p.free) return p.trade_ok ? 'Free · or trade' : 'Free';
  const hasPrice = p.price != null && p.price !== '' && Number(p.price) > 0;
  if (hasPrice && p.trade_ok) return `$${Number(p.price)} · or trade`;
  if (hasPrice) return `$${Number(p.price)}`;
  if (p.trade_ok) return 'Trade';
  return 'Ask';
}

// Remembered manual location (ZIP/city search) — device-local only.
const NEAR_LOC_KEY = 'aqs.nearLoc';
export function readSavedNearLoc() {
  try {
    const raw = localStorage.getItem(NEAR_LOC_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!Number.isFinite(o?.lat) || !Number.isFinite(o?.lng)) return null;
    return { ...o, area: validArea(o.area) };
  } catch {
    return null;
  }
}
export function writeSavedNearLoc(o) {
  try {
    if (o) localStorage.setItem(NEAR_LOC_KEY, JSON.stringify(o));
    else localStorage.removeItem(NEAR_LOC_KEY);
  } catch {
    /* private mode — best effort */
  }
}

function validArea(a) {
  const b = a?.bbox;
  if (!a || typeof a.name !== 'string' || !a.name || !b) return null;
  const nums = [b.minLat, b.minLng, b.maxLat, b.maxLng];
  if (!nums.every(Number.isFinite)) return null;
  return { name: a.name, code: typeof a.code === 'string' ? a.code : null, bbox: b };
}

// The discovery feed's address: a radius round a point, or (a typed
// state) the state's own box, listed whole.
export function nearFeedUrl(coords, { radius = 50, area = null } = {}) {
  const base = `/api/near?lat=${coords.lat}&lng=${coords.lng}`;
  const a = validArea(area);
  if (!a) return `${base}&radius=${radius}`;
  const { minLat, minLng, maxLat, maxLng } = a.bbox;
  const code = a.code ? `&area_code=${encodeURIComponent(a.code)}` : '';
  return `${base}&area=${encodeURIComponent(a.name)}${code}&bbox=${[minLat, minLng, maxLat, maxLng].join(',')}`;
}

// A new start for an event being written: the end moves with it and the
// event keeps its length (3 hours when the end is missing or not after the
// start). Judged against the old start, the form's own end never moved: a
// swap picked for next month still ended this Saturday and the server
// refused it ("can't end before it starts"), and moving a start a week back
// made a week-long event.
export function moveEventStart(f, v) {
  const oldStart = new Date(f.starts_at).getTime();
  const oldEnd = new Date(f.ends_at).getTime();
  const next = new Date(v).getTime();
  const length = oldEnd - oldStart;
  if (!v || !Number.isFinite(next)) return { ...f, starts_at: v };
  if (!Number.isFinite(length) || length <= 0)
    return { ...f, starts_at: v, ends_at: plusHours(v, 3) };
  return { ...f, starts_at: v, ends_at: toLocalInput(new Date(next + length)) };
}
